import { $t } from '@/i18n';
import { set, forEach, get } from 'lodash';
import { message, Modal, notification } from 'ant-design-vue';
import {
  XyModalSelectorMeta, XyPageletPropsEditorMeta, XyGlobalModalSettingMeta,
} from '../paramsFormComponents';
import { stringInterop, vueDeepSet } from '@tencent/ui-core/lib/utils';
import { toPathParts } from '@tencent/data-schema-core/lib/utils/path';
import { toString } from '@/utils/toString';
import { CATEGORY } from './const';
import pageLoader from '@loaders/page/loader';

// 关闭弹窗
export const getXyHideModalAtom = type => ({
  id: 'xy:hideModal',
  name: $t('k_de91ca00'),
  category: CATEGORY.UI,
  visible: type === 'modal',
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'data',
        type: 'array',
        title: $t('k_5a006242'),
        'uc:allowInterop': true,
        ui: {
          type: 'XyPageletPropsEditor',
          props: {
            keyIsInteropable: true,
          },
        },
        items: {
          type: 'object',
          fields: [
            { id: 'key', type: 'string' },
            { id: 'value', type: 'string' },
          ],
        },
      },
    ],
  },
  paramsFormComponents: [
    XyPageletPropsEditorMeta,
  ],
  async execute(ctx, params) {
    const { wContext, w } = ctx.renderer;  // xy 上下文
    const { data } = params;
    const hideData = {};
    if (Array.isArray(data)) {
      data.forEach((item) => {
        const { key, value } = item;
        hideData[key] = value;
      });
    }

    if (wContext?.type === 'globalModal') {
      await w.hideGlobalModal(wContext.id, hideData);
    } else {
      await wContext.hideModal(null, hideData);
    }
  },
});
// 弹出消息窗口
export const alertAtom = {
  id: 'uicore:alert',
  name: $t('k_a7445e17'),
  category: $t('k_932ce511'),
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'text',
        type: 'string',
        title: $t('k_20a7ae71'),
        default: $t('k_c24d22cd'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
      {
        id: 'icon',
        type: 'string',
        title: $t('k_5aec5cc9'),
        enum: [
          { value: '', label: $t('k_aa889a09') },
          { value: 'info' },
          { value: 'success' },
          { value: 'error' },
          { value: 'warning' },
        ],
      },
      {
        id: 'width',
        type: 'string',
        title: $t('k_a80ac70b'),
        description: $t('k_53bbde8f'),
      },
    ],
  },
  execute: async (ctx, params) => {
    const text = toString(params.text).trim();
    if (!text) throw new Error($t('k_c24d22cd'));

    return new Promise((resolve) => {
      const modalOption = {
        content: h => h('div', { style: { maxWidth: '80vw', whiteSpace: 'pre-wrap' } }, [text]),
        maskClosable: true,
        onOk: () => void resolve(),
        onCancel: () => void resolve(),
      };
      if (params.width) set(modalOption, 'width', params.width);
      // 无图标则渲染空的 VNode, 否则使用 Modal 方法对应的图标
      if (!params.icon) modalOption.icon = h => h();
      Modal[params.icon || 'info'](modalOption);
    });
  },
};
// 弹出确认框
export const confirmAtom = {
  id: 'uicore:confirm',
  name: $t('k_e0784cdf'),
  category: CATEGORY.UI,
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'text',
        type: 'string',
        title: $t('k_3a49de55'),
        default: $t('k_d312931f'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
      {
        id: 'abortIfRejected',
        type: 'boolean',
        title: $t('k_97105ad3'),
        default: true,
      },
      {
        id: 'okText',
        type: 'string',
        title: $t('k_6b16c907'),
        default: $t('k_c7ec97ab'),
      },
      {
        id: 'cancelText',
        type: 'string',
        title: $t('k_c60fa5d0'),
        default: $t('k_9e48cac4'),
      },
      {
        id: 'icon',
        title: $t('k_5aec5cc9'),
        type: 'string',
        enum: [
          { value: 'question' },
          { value: 'question-circle' },
          { value: 'info' },
          { value: 'info-circle' },
          { value: 'check' },
          { value: 'check-circle' },
          { value: 'exclamation' },
          { value: 'exclamation-circle' },
          { value: 'close' },
          { value: 'close-circle' },
          { value: 'stop' },
          { value: 'warning' },
          { value: 'delete' },
        ],
        default: 'question-circle',
      },
      {
        id: 'centered',
        title: $t('k_dc53da43'),
        type: 'boolean',
        default: false,
      },
    ],
  },
  returnValue: { type: 'boolean', description: $t('k_9ed6bbd6') },
  execute: async (ctx, params) => {
    const { text: content, okText, cancelText, icon, centered = false, abortIfRejected } = params;
    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        content: () => <div domPropsInnerHTML={toString(content)}></div>,
        okText: toString(okText),
        cancelText: toString(cancelText),
        icon: toString(icon),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
        centered,
      });
    });
    if (!confirmed && abortIfRejected) ctx.abort();
    return confirmed;
  },
};
// 弹出提示
export const messageAtom = {
  id: 'uicore:message',
  name: $t('k_1b7a7f0'),
  category: CATEGORY.UI,
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'type',
        title: $t('k_ebe72edb'),
        type: 'string',
        enum: [
          { value: 'info' },
          { value: 'error' },
          { value: 'success' },
          { value: 'warn' },
          { value: 'loading' },
        ],
        default: 'info',
      },
      {
        id: 'duration',
        title: $t('k_c3cfc45f'),
        type: 'integer',
        default: 3,
      },
      {
        id: 'isSync',
        title: $t('k_6426c44'),
        type: 'boolean',
        default: false,
      },
      {
        id: 'text',
        type: 'string',
        title: $t('k_7b4c99db'),
        default: $t('k_f9f2ba58'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
    ],
  },
  execute: async (ctx, params) => {
    const { type = 'info', duration = 3, isSync = false } = params;
    const text = toString(params.text);
    const content = h => h('span', {
      style: {
        display: 'inline-block',
        'white-space': 'pre-wrap',
        'vertical-align': 'top',
        'text-align': 'left',
      },
    }, [text]);
    const promise = message[type](content, duration);

    if (isSync) await promise;
  },
};
// 弹出通知框
export const notificationAtom = {
  id: 'uicore:notification',
  name: $t('k_b6200d91'),
  category: CATEGORY.UI,
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'type',
        title: $t('k_ebe72edb'),
        type: 'string',
        enum: [
          { value: 'open' },
          { value: 'error' },
          { value: 'success' },
          { value: 'warn' },
          { value: 'error' },
        ],
        default: 'open',
      },
      {
        id: 'placement',
        title: $t('k_ca6b5c1f'),
        type: 'string',
        enum: [
          { value: 'topLeft', label: $t('k_7485784b') },
          { value: 'topRight', label: $t('k_d55d16f8') },
          { value: 'bottomLeft', label: $t('k_cf7162dc') },
          { value: 'bottomRight', label: $t('k_3d093e98') },
        ],
        default: 'topRight',
      },
      {
        id: 'duration',
        title: $t('k_c3cfc45f'),
        type: 'number',
        default: 4.5,
      },
      {
        id: 'message',
        type: 'string',
        title: $t('k_3fa7cb03'),
        default: $t('k_3fa7cb03'),
      },
      {
        id: 'description',
        type: 'string',
        title: $t('k_35433cae'),
        default: $t('k_373379db'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
    ],
  },
  execute: async (ctx, params) => {
    const { type = 'info', placement, message, description, duration, isSync } = params;

    const options = {
      placement,
      message: () => <div domPropsInnerHTML={toString(message)}></div>,
      description: () => <div domPropsInnerHTML={toString(description)}></div>,
      duration,
      isSync,
    };

    notification[type](options);
  },
};

export const promptAtom = {
  id: 'uicore:prompt',
  name: $t('k_2a72389b'),
  category: $t('k_932ce511'),
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'message',
        type: 'string',
        title: $t('k_7803dae9'),
        default: $t('k_20f56626'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
      {
        id: 'defaultValue',
        type: 'string',
        title: $t('k_ba45e89f'),
      },
      // 兼容旧字段, 当现存勾选了 password 时, 外显配置让用户反选后再切换输入框模式
      {
        id: 'password',
        type: 'boolean',
        title: $t('k_986b06a6'),
        condition: '!!params.password',
      },
      {
        id: 'inputType',
        type: 'string',
        title: $t('k_8c33a6ff'),
        enum: [
          { value: 'input', label: $t('k_fd24931') },
          { value: 'textarea', label: $t('k_2c43a2e9') },
          { value: 'password', label: $t('k_cbaddf0a') },
        ],
      },
      {
        id: 'allowEmpty',
        type: 'boolean',
        title: $t('k_d9335985'),
      },
      {
        id: 'abortIfRejected',
        type: 'boolean',
        title: $t('k_6eb9032f'),
        default: true,
      },
    ],
  },
  returnValue: { type: 'string', description: $t('k_a6cef13b') },
  execute: async (ctx, params) => {
    const { uc } = ctx;
    while (true) {
      // 兼容旧字段 password 优先级最高
      const password = !!params.password || params.inputType === 'password';
      const textarea = params.inputType === 'textarea';

      const answer = await uc?.ui?.prompt({
        message: toString(params.message),
        defaultValue: toString(params.defaultValue),
        password,
        textarea,
      });

      if (answer === null) {
        if (params.abortIfRejected) ctx.abort();
        return params.defaultValue;
      }

      if (answer === '' && !params.allowEmpty) continue;

      return answer;
    }
  },
};

// 打开弹窗
export const showModalAtom = {
  id: 'xy:showModal',
  name: $t('k_cef231d3'),
  category: CATEGORY.UI,
  desc: $t('k_6b381d6'),
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'modalId',
        type: 'string',
        title: $t('k_88ec7b8b'),
        ui: { type: 'XyModalSelector' },
      },
      {
        id: 'globalModalSetting',
        type: 'object',
        title: $t('k_bc4829c5'),
        ui: { type: 'XyGlobalModalSetting' },
        default: {
          keepAlive: false,
          suffix: '',
        },
        condition: 'params.modalId.includes(":")',
      },
      {
        id: 'data',
        type: 'array',
        title: $t('k_92f695df'),
        ui: {
          type: 'XyPageletPropsEditor',
          props: {
            allowBind: true,
            async getPagelet(inputBox) {
              const { modalId } = inputBox.container.stub.parent.value;
              const isModalPage = modalId?.includes(':');
              if (isModalPage) {
                const [, pageId] = modalId.split(':');
                const { projectId } = inputBox.wContext;
                const { pageConfig: pagelets } = await pageLoader.getPageDetail({
                  projectId,
                  pageId,
                });
                return pagelets?.find(x => x.id === 'default');
              }
              const allModalPagelets = inputBox.wContext.modalList;
              return allModalPagelets.find(x => x.id === modalId);
            },
          },
        },
        items: {
          type: 'object',
          fields: [
            { id: 'key', type: 'string' },
            { id: 'value', type: 'string' },
            { id: 'bind', type: 'string' },
          ],
        },
        'uc:allowExpr': false,
      },
    ],
  },
  paramsFormComponents: [
    XyModalSelectorMeta,
    XyPageletPropsEditorMeta,
    XyGlobalModalSettingMeta,
  ],
  async execute(ctx, params) {
    const { renderer } = ctx;
    const { wContext, w } = renderer;  // xy 上下文
    // [废弃] mergeParams 兼容旧配置
    const { modalId, data, mergeParams, globalModalSetting } = params;
    const dynamicData = {};
    const propsData = {};

    const mixedContext = new Proxy(renderer, {
      get(t, k) {
        if (k === 'flow') return ctx;
        if (k === 'flowState') return ctx.state;
        if (k === '$event') return ctx.$event;

        return Reflect.get(t, k);
      },
    });

    forEach(data, (paramItem) => {
      const { key, value: valueRaw, bind } = paramItem;

      if (bind) {
        // 使用双向绑定的方式结合到当前页面的数据源
        const bindParts = toPathParts(bind);
        Object.defineProperty(dynamicData, key, {
          configurable: false,
          enumerable: true,
          get: () => get(mixedContext, bindParts),
          set: (value) => {
            vueDeepSet(mixedContext, bindParts, value);
          },
        });
      } else {
        // 值传递
        Object.defineProperty(dynamicData, key, {
          configurable: false,
          enumerable: true,
          get: () => {
            if (Object.prototype.hasOwnProperty.call(propsData, key)) return propsData[key];
            return stringInterop(valueRaw, renderer, { flow: ctx, flowState: ctx.state, $event: ctx.$event });
          },
          set: (value) => {
            propsData[key] = value;
          },
        });
      }
    });

    const isModalPage = modalId?.includes(':');
    const [, pageId] = modalId?.split(':');
    const closeRes = isModalPage
      ? await w.showGlobalModal(pageId, dynamicData, globalModalSetting)
      : await wContext.showModal(modalId, dynamicData, { mergeParams, params: data });
    return closeRes;
  },
  returnValue: {
    description: $t('k_e1d734d2'),
  },
};

export default [
  alertAtom,
  confirmAtom,
  messageAtom,
  notificationAtom,
  promptAtom,
  showModalAtom,
];
